import { useMemo } from "react";
import { useWallet } from "../../wallet/WalletContext";
import { ACTIVE_CHAIN } from "../../config/chains";
import { resolveLiveConfig, type LiveConfig } from "../../config/liveConfig";
import type { LauncherClient } from "./launcherClient";
import { MockLauncherClient, type MockScenario } from "./mockLauncherClient";
import { ViemLauncherClient } from "./viemLauncherClient";

export interface UseLauncher {
  client: LauncherClient;
  /** True when no factory is configured — deployment must stay disabled. */
  isDemo: boolean;
  /** True in the dedicated E2E build (scenarios enabled). */
  isE2E: boolean;
  /** Non-null only under the E2E build's scenario mechanism. */
  testScenario: MockScenario | null;
  /** Set when live config is malformed/incomplete — blocks deployment. */
  configError: { field: string; message: string } | null;
  /** The active chain's deployment block, when live-configured. */
  deploymentBlock: bigint | null;
}

const VALID_SCENARIOS: MockScenario[] = [
  "publicPays", "teamExempt", "wrongNetwork", "notConfigured", "paused",
  "bytecodeMismatch", "userRejected", "pending", "reverted", "replaced",
  "insufficientBalance", "feeChanged", "successReceipt", "postVerificationFailed",
  "ownerAdmin", "ownerAdminExempt", "adminEventMismatch", "adminFinalMismatch",
];

/**
 * E2E-ONLY. Scenario handling is enabled ONLY when the build was produced with
 * VITE_ENABLE_E2E_SCENARIOS=true (a compile-time flag inlined by Vite). Ordinary
 * development, Preview, and production builds leave it unset, so scenarios are off
 * regardless of MODE. This is not gated on MODE.
 */
export function e2eScenariosEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_E2E_SCENARIOS === "true";
}

function readTestScenario(): MockScenario | null {
  if (!e2eScenariosEnabled()) return null;
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("scenario");
  return raw && (VALID_SCENARIOS as string[]).includes(raw) ? (raw as MockScenario) : null;
}

export function activeLiveConfig(): LiveConfig {
  return resolveLiveConfig({
    factoryAddress: ACTIVE_CHAIN.factoryAddress,
    expectedHash: ACTIVE_CHAIN.expectedFactoryBytecodeHash,
    deploymentBlock: ACTIVE_CHAIN.factoryDeploymentBlock,
    rawDeploymentBlock: import.meta.env.VITE_TESTNET_FACTORY_DEPLOYMENT_BLOCK,
    deploymentTx: ACTIVE_CHAIN.factoryDeploymentTx,
    chainId: ACTIVE_CHAIN.chainId,
  });
}

export function useLauncher(): UseLauncher {
  const { publicClient, walletClient } = useWallet();

  return useMemo(() => {
    const isE2E = e2eScenariosEnabled();
    const base = { isE2E, testScenario: null, configError: null, deploymentBlock: null } as const;

    if (isE2E) {
      const scenario = readTestScenario();
      if (scenario) {
        // Configured mock so Deploy is reachable in E2E; every result is demo data.
        return { ...base, client: new MockLauncherClient({ scenario }), isDemo: false, testScenario: scenario };
      }
      return { ...base, client: new MockLauncherClient(), isDemo: true };
    }

    const cfg = activeLiveConfig();
    if (cfg.status === "demo") {
      return { ...base, client: new MockLauncherClient(), isDemo: true };
    }
    if (cfg.status === "error") {
      // Blocked configuration-error mode: never instantiate the real client, never
      // enable deployment. A safe mock keeps pages renderable but demo-flagged.
      return {
        ...base,
        client: new MockLauncherClient({ scenario: "notConfigured" }),
        isDemo: true,
        configError: { field: cfg.field, message: cfg.message },
      };
    }
    // Live.
    const client = new ViemLauncherClient({
      publicClient,
      walletClient: walletClient ?? undefined,
      factoryAddress: cfg.factoryAddress,
      expectedBytecodeHash: cfg.expectedHash,
      chainId: cfg.chainId,
      deploymentBlock: cfg.deploymentBlock,
    });
    return { ...base, client, isDemo: false, deploymentBlock: cfg.deploymentBlock };
  }, [publicClient, walletClient]);
}
